<template>
  <v-app>
    <!-- Mobile Navigation Drawer -->
    <vertical-nav-menu :is-drawer-open.sync="isDrawerOpen"></vertical-nav-menu>

    <!-- Top Horizontal Navigation Bar -->
    <v-app-bar
      app
      flat
      color="white"
      class="app-bar-bg"
      height="70"
    >
      <div class="boxed-container w-full">
        <div class="d-flex align-center mx-3">
          <!-- Mobile Hamburger Drawer Icon -->
          <v-app-bar-nav-icon
            class="d-block d-lg-none me-2"
            @click="isDrawerOpen = !isDrawerOpen"
          ></v-app-bar-nav-icon>

          <!-- Desktop Logo -->
          <div class="d-none d-lg-flex align-center me-3">
            <router-link to="/" class="d-flex align-center text-decoration-none">
              <v-img
                :src="require('@/assets/images/logos/logo.png')"
                max-height="45px"
                max-width="130px"
                alt="logo"
                contain
                eager
                class="me-1"
              ></v-img>
            </router-link>
          </div>

          <!-- Desktop Horizontal Navigation Menu -->
          <div class="d-none d-lg-flex align-center flex-grow-1 nav-menu-container">
            <!-- Operations (role = 4): approved screens "الرئيسية" and "السائقين" -->
            <template v-if="isOperations">
              <v-btn
                text
                to="/operations"
                class="nav-item text-capitalize font-weight-bold"
                active-class="active-nav-item"
                exact
              >
                <v-icon left size="16">mdi-view-dashboard-outline</v-icon>
                الرئيسية
              </v-btn>
              <v-btn
                text
                to="/operations/drivers"
                class="nav-item text-capitalize font-weight-bold"
                active-class="active-nav-item"
              >
                <v-icon left size="16">mdi-card-account-details-outline</v-icon>
                السائقين
              </v-btn>
              <v-btn
                text
                to="/operations/vehicles"
                class="nav-item text-capitalize font-weight-bold"
                active-class="active-nav-item"
              >
                <v-icon left size="16">mdi-bus</v-icon>
                وسائل النقل
              </v-btn>
              <v-btn
                text
                to="/operations/routes"
                class="nav-item text-capitalize font-weight-bold"
                :class="{ 'active-nav-item': isOperationsRoutesActive }"
              >
                <v-icon left size="16">mdi-account-tie-outline</v-icon>
                تعيين السائقين
              </v-btn>
              <v-btn
                text
                to="/operations/trips"
                class="nav-item text-capitalize font-weight-bold"
                :class="{ 'active-nav-item': isOperationsTripsActive }"
              >
                <v-icon left size="16">mdi-bus-clock</v-icon>
                الرحلات
              </v-btn>
              <v-btn
                text
                to="/operations/live-tracking"
                class="nav-item text-capitalize font-weight-bold"
                :class="{ 'active-nav-item': isOperationsLiveTrackingActive }"
              >
                <v-icon left size="16">mdi-crosshairs-gps</v-icon>
                التتبع المباشر
              </v-btn>
              <v-btn
                text
                to="/operations/support"
                class="nav-item text-capitalize font-weight-bold"
                :class="{ 'active-nav-item': isOperationsSupportActive }"
              >
                <v-icon left size="16">mdi-headset</v-icon>
                الدعم والتذاكر
              </v-btn>
              <v-btn
                text
                to="/operations/emergency-requests"
                class="nav-item text-capitalize font-weight-bold"
                :class="{ 'active-nav-item': isOperationsEmergencyActive }"
              >
                <v-icon left size="16">mdi-alert-octagon-outline</v-icon>
                طلبات إلغاء الطوارئ
              </v-btn>
            </template>

            <!-- Route Making (role = 3): ONLY approved screens "المسارات" and "الرحلات" -->
            <template v-else-if="isRouteMaking">
              <v-btn
                text
                to="/routes"
                class="nav-item text-capitalize font-weight-bold"
                :class="{ 'active-nav-item': isRouteMakingRouteActive }"
              >
                <v-icon left size="16">mdi-road-variant</v-icon>
                {{ $t('Routes') }}
              </v-btn>
              <v-btn
                text
                to="/trips"
                class="nav-item text-capitalize font-weight-bold"
                :class="{ 'active-nav-item': isRouteMakingTripsActive }"
              >
                <v-icon left size="16">mdi-bus-clock</v-icon>
                {{ $t('trips') }}
              </v-btn>
              <v-btn
                text
                to="/trip-groups"
                class="nav-item text-capitalize font-weight-bold"
                :class="{ 'active-nav-item': isTripGroupsActive }"
              >
                <v-icon left size="16">mdi-folder-multiple-outline</v-icon>
                مجموعات الرحلات
              </v-btn>
            </template>

            <!-- Customer Service (role = 5): Approved screens "العملاء", "الحجوزات", "الاستردادات", and "التذاكر" -->
            <template v-else-if="isCustomerService">
              <v-btn
                text
                to="/customer-service/customers"
                class="nav-item text-capitalize font-weight-bold"
                active-class="active-nav-item"
                :class="{ 'active-nav-item': isCustomerServiceCustomersActive }"
              >
                <v-icon left size="16">mdi-account-group</v-icon>
                العملاء
              </v-btn>
              <v-btn
                text
                to="/customer-service/bookings"
                class="nav-item text-capitalize font-weight-bold"
                active-class="active-nav-item"
                :class="{ 'active-nav-item': isCustomerServiceBookingsActive }"
              >
                <v-icon left size="16">mdi-ticket-confirmation-outline</v-icon>
                الحجوزات
              </v-btn>
              <v-btn
                text
                to="/customer-service/refunds"
                class="nav-item text-capitalize font-weight-bold"
                active-class="active-nav-item"
                :class="{ 'active-nav-item': isCustomerServiceRefundsActive }"
              >
                <v-icon left size="16">mdi-cash-refund</v-icon>
                الاستردادات
              </v-btn>
              <v-btn
                text
                to="/customer-service/tickets"
                class="nav-item text-capitalize font-weight-bold"
                active-class="active-nav-item"
                :class="{ 'active-nav-item': isCustomerServiceTicketsActive }"
              >
                <v-icon left size="16">mdi-ticket-account</v-icon>
                التذاكر
              </v-btn>
            </template>

            <!-- Super Admin (role = 0): Original full navigation UNCHANGED -->
            <template v-else-if="isSuperAdmin">
              <!-- Dashboard Link -->
              <v-btn
                text
                :to="{ name: 'dashboard' }"
                class="nav-item text-capitalize font-weight-bold"
                active-class="active-nav-item"
                exact
              >
                <v-icon left size="16">mdi-home-outline</v-icon>
                {{ $t('dashboard') }}
              </v-btn>

              <!-- Dropdown Menus Loop -->
              <template v-for="menu in navMenus">
                <!-- Direct Menu Item (e.g. Reservations) -->
                <v-btn
                  v-if="menu.isDirect"
                  :key="menu.title"
                  text
                  :to="menu.to"
                  class="nav-item text-capitalize font-weight-bold"
                  active-class="active-nav-item"
                  exact
                >
                  <v-icon left size="16">{{ menu.icon }}</v-icon>
                  {{ $t(menu.title) || menu.title }}
                </v-btn>

              <!-- Dropdown Menu Item (Buses, Routes, Stops, Trips, Regular Subscriptions, Payments, Users, Private Car Booking, More) -->
              <v-menu
                v-else
                :key="menu.title"
                open-on-hover
                offset-y
                transition="slide-y-transition"
                content-class="nav-menu-dropdown"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    text
                    v-bind="attrs"
                    v-on="on"
                    class="nav-item text-capitalize font-weight-bold"
                    active-class="active-nav-item"
                  >
                    <v-icon left size="16">{{ menu.icon }}</v-icon>
                    {{ $t(menu.title) || menu.title }}
                    <v-icon right size="12">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list dense class="py-1">
                  <template v-for="(item, idx) in menu.items">
                    <!-- Divider -->
                    <v-divider v-if="item.isDivider" :key="'div-' + idx" class="my-1"></v-divider>

                    <!-- Subheader -->
                    <v-subheader
                      v-else-if="item.isHeader"
                      :key="'hdr-' + idx"
                      class="text-uppercase font-weight-bold text-xxs primary--text px-4 py-0"
                      style="height: 24px; font-size: 0.65rem;"
                    >
                      {{ $t(item.title) || item.title }}
                    </v-subheader>

                    <!-- Normal Menu List Item -->
                    <v-list-item
                      v-else
                      :key="item.title"
                      :to="item.to"
                      active-class="primary--text font-weight-bold"
                      exact
                    >
                      <v-list-item-icon class="me-2 my-1">
                        <v-icon size="16" color="primary">{{ item.icon }}</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-1">
                        <v-list-item-title class="text-caption font-weight-medium">
                          {{ $t(item.title) || item.title }}
                          <span v-if="item.isLive" class="live-dot ms-1"></span>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list>
              </v-menu>
            </template>
            </template>
          </div>

          <v-spacer></v-spacer>
          
          <!-- Right Content Controls -->
          <div class="d-flex align-center">
            <lang-switcher></lang-switcher>
            <!-- <theme-switcher></theme-switcher> -->
            
            <!-- Notifications Menu (Super Admin, Customer Service & Operations Role 4) -->
            <v-menu
              v-if="isSuperAdmin || isCustomerService || isOperations"
              offset-y
              left
              nudge-bottom="14"
              min-width="230"
              content-class="user-profile-menu-content"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="ms-2"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-badge
                    v-if="isCsNotificationMode ? (unreadCsNotifsCount > 0) : (isOpsNotificationMode ? (unreadOpsNotifsCount > 0) : (driversNotification.count != 0 || complaintsNotification.count != 0 || emergencyNotification.count != 0 || documentUpdatesNotification.count != 0 || lateStartNotification.count != 0 || overdueMovementNotification.count != 0 || staleTrackingNotification.count != 0))"
                    color="error"
                    :content="isCsNotificationMode ? unreadCsNotifsCount : (isOpsNotificationMode ? unreadOpsNotifsCount : (driversNotification.count + complaintsNotification.count + emergencyNotification.count + documentUpdatesNotification.count + lateStartNotification.count + overdueMovementNotification.count + staleTrackingNotification.count))"
                  >
                    <v-icon>{{ icons.mdiBellOutline }}</v-icon>
                  </v-badge>
                  <v-icon v-else>{{ icons.mdiBellOutline }}</v-icon>
                </v-btn>
              </template>

              <!-- Role 5 Customer Service Notification Center -->
              <v-card v-if="isCsNotificationMode" width="410" max-width="95vw" class="elevation-6 rounded-xl cs-notification-card">
                <!-- Card Header -->
                <div class="cs-notif-header px-4 py-3 d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <div class="cs-notif-header-icon me-2">
                      <v-icon small color="#082C69">mdi-bell-ring-outline</v-icon>
                    </div>
                    <div>
                      <div class="text-subtitle-2 font-weight-bold navy--text mb-0 line-height-tight">مركز الإشعارات</div>
                      <div class="text-caption text-muted" style="font-size: 11px !important;">خدمة العملاء (Role 5)</div>
                    </div>
                  </div>
                  <div class="d-flex align-center">
                    <span
                      v-if="unreadCsNotifsCount > 0"
                      class="cs-unread-counter-badge me-2"
                    >
                      {{ unreadCsNotifsCount }} جديد
                    </span>
                    <v-btn
                      x-small
                      text
                      color="#082C69"
                      class="font-weight-bold mark-all-btn px-2 rounded-md"
                      :disabled="unreadCsNotifsCount === 0"
                      @click.stop="markAllCsNotificationsSeen"
                    >
                      <v-icon left x-small>mdi-check-all</v-icon>
                      تعليم الكل كمقروء
                    </v-btn>
                  </div>
                </div>

                <!-- Sub-bar KPI quick links -->
                <div class="cs-notif-subbar d-flex align-center justify-space-around py-2 px-3">
                  <router-link to="/customer-service/tickets" class="cs-subbar-link d-flex align-center text-decoration-none">
                    <v-icon x-small color="#082C69" class="me-1">mdi-ticket-account</v-icon>
                    <span class="text-caption font-weight-bold navy--text">تذاكر الدعم:</span>
                    <span class="text-caption font-weight-bold ms-1" style="color: #0369A1;">{{ openTicketsCount }} مفتوحة</span>
                  </router-link>
                  <v-divider vertical class="mx-2 my-1"></v-divider>
                  <router-link to="/customer-service/refunds" class="cs-subbar-link d-flex align-center text-decoration-none">
                    <v-icon x-small color="#B45309" class="me-1">mdi-cash-refund</v-icon>
                    <span class="text-caption font-weight-bold" style="color: #92400E;">الاستردادات:</span>
                    <span class="text-caption font-weight-bold ms-1" style="color: #D97706;">{{ pendingRefundsCount }} معلقة</span>
                  </router-link>
                </div>

                <!-- Notifications List (Scrollable, latest 10-15) -->
                <div class="cs-notif-list-container" v-if="csNotifications.length > 0">
                  <div
                    v-for="(item, idx) in csNotifications"
                    :key="'cs-notif-' + item.id"
                    class="cs-notif-item px-3 py-3 cursor-pointer"
                    :class="{ 'cs-notif-unread': item.seen === 0, 'cs-notif-read': item.seen === 1 }"
                    @click="markCsNotificationSeen(item)"
                  >
                    <div class="d-flex align-start">
                      <!-- Semantic Icon Avatar -->
                      <div
                        class="cs-notif-avatar me-3 mt-1"
                        :class="item.related_type === 'refund' || item.type === 'refund' ? 'avatar-refund' : 'avatar-ticket'"
                      >
                        <v-icon size="18" :color="item.related_type === 'refund' || item.type === 'refund' ? '#B45309' : '#082C69'">
                          {{ (item.related_type === 'refund' || item.type === 'refund') ? 'mdi-cash-refund' : (item.event_type === 'customer_reply' ? 'mdi-message-reply-text' : 'mdi-ticket-account') }}
                        </v-icon>
                      </div>

                      <!-- Content Area -->
                      <div class="flex-grow-1 min-w-0">
                        <!-- Top Row: Source Badge + Code + Urgent + Time -->
                        <div class="d-flex align-center justify-space-between mb-1">
                          <div class="d-flex align-center flex-wrap gap-1">
                            <!-- Source Badge -->
                            <span
                              class="cs-source-badge"
                              :class="item.related_type === 'refund' || item.type === 'refund' ? 'source-badge-refund' : 'source-badge-ticket'"
                            >
                              {{ (item.related_type === 'refund' || item.type === 'refund') ? 'استرداد' : 'تذكرة' }}
                            </span>

                            <!-- Exact Reference Code -->
                            <span v-if="item.reference_code" class="mono-code cs-ref-code font-weight-bold ms-1">
                              {{ item.reference_code }}
                            </span>

                            <!-- Attention / Urgent priority indicator -->
                            <span v-if="item.is_urgent" class="cs-urgent-badge ms-1">
                              عاجل
                            </span>
                          </div>

                          <div class="d-flex align-center">
                            <span class="cs-notif-time text-caption text-muted ms-2">
                              {{ item.time_ago || item.created_at }}
                            </span>
                            <span v-if="item.seen === 0" class="cs-unread-indicator-dot ms-1" title="غير مقروء"></span>
                          </div>
                        </div>

                        <!-- Title -->
                        <div class="cs-notif-title text-caption font-weight-bold text-dark text-truncate">
                          {{ item.title }}
                        </div>

                        <!-- Description / Body -->
                        <div class="cs-notif-body text-caption text-muted">
                          {{ item.message }}
                        </div>

                        <!-- Extra Context (Customer Name / Amount) -->
                        <div v-if="item.customer_name || item.amount" class="cs-notif-meta d-flex align-center mt-1">
                          <span v-if="item.customer_name" class="d-flex align-center me-2 text-caption">
                            <v-icon x-small color="#64748B" class="me-1">mdi-account-outline</v-icon>
                            {{ item.customer_name }}
                          </span>
                          <span v-if="item.amount" class="d-flex align-center text-caption font-weight-bold" style="color: #082C69;">
                            <v-icon x-small color="#082C69" class="me-1">mdi-cash</v-icon>
                            {{ item.amount }} ج.م
                          </span>
                        </div>
                      </div>
                    </div>
                    <v-divider v-if="idx < csNotifications.length - 1" class="mt-3"></v-divider>
                  </div>
                </div>

                <!-- Empty State -->
                <div v-else class="text-center py-8 px-4">
                  <div class="cs-empty-icon-circle mx-auto mb-2">
                    <v-icon size="32" color="#94A3B8">mdi-bell-check-outline</v-icon>
                  </div>
                  <div class="text-subtitle-2 font-weight-bold navy--text">لا توجد إشعارات جديدة</div>
                  <div class="text-caption text-muted mt-1">
                    ستظهر هنا الإشعارات الواردة بخصوص التذاكر وطلبات الاسترداد فور وصولها.
                  </div>
                </div>

                <!-- Card Footer -->
                <div class="cs-notif-footer px-3 py-2 d-flex align-center justify-space-between border-top">
                  <router-link to="/customer-service/tickets" class="text-caption font-weight-bold navy--text text-decoration-none">
                    <v-icon x-small color="#082C69" class="me-1">mdi-format-list-bulleted</v-icon>
                    جميع التذاكر
                  </router-link>
                  <router-link to="/customer-service/refunds" class="text-caption font-weight-bold" style="color: #B45309; text-decoration: none;">
                    <v-icon x-small color="#B45309" class="me-1">mdi-cash-multiple</v-icon>
                    جميع الاستردادات
                  </router-link>
                </div>
              </v-card>

              <!-- Role 4 Operations Notification Center -->
              <v-card v-else-if="isOpsNotificationMode" width="420" max-width="95vw" class="elevation-6 rounded-xl cs-notification-card">
                <!-- Card Header -->
                <div class="cs-notif-header px-4 py-3 d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <div class="cs-notif-header-icon me-2">
                      <v-icon small color="#082C69">mdi-bell-ring-outline</v-icon>
                    </div>
                    <div>
                      <div class="text-subtitle-2 font-weight-bold navy--text mb-0 line-height-tight">مركز الإشعارات</div>
                      <div class="text-caption text-muted" style="font-size: 11px !important;">التشغيل ودعم السائقين (Role 4)</div>
                    </div>
                  </div>
                  <div class="d-flex align-center">
                    <span
                      v-if="unreadOpsNotifsCount > 0"
                      class="cs-unread-counter-badge me-2"
                    >
                      {{ unreadOpsNotifsCount }} جديد
                    </span>
                    <v-btn
                      x-small
                      text
                      color="#082C69"
                      class="font-weight-bold mark-all-btn px-2 rounded-md"
                      :disabled="unreadOpsNotifsCount === 0"
                      @click.stop="markAllOpsNotificationsSeen"
                    >
                      <v-icon left x-small>mdi-check-all</v-icon>
                      تعليم الكل كمقروء
                    </v-btn>
                  </div>
                </div>

                <!-- Sub-bar KPI quick links -->
                <div class="cs-notif-subbar d-flex align-center justify-space-around py-2 px-3">
                  <router-link to="/operations/support" class="cs-subbar-link d-flex align-center text-decoration-none">
                    <v-icon x-small color="#D97706" class="me-1">mdi-clock-alert-outline</v-icon>
                    <span class="text-caption font-weight-bold" style="color: #92400E;">غير مستلمة:</span>
                    <span class="text-caption font-weight-bold ms-1" style="color: #D97706;">{{ unassignedDriverTicketsCount }}</span>
                  </router-link>
                  <v-divider vertical class="mx-2 my-1"></v-divider>
                  <router-link to="/operations/support" class="cs-subbar-link d-flex align-center text-decoration-none">
                    <v-icon x-small color="#047857" class="me-1">mdi-account-check-outline</v-icon>
                    <span class="text-caption font-weight-bold" style="color: #065F46;">تذاكري:</span>
                    <span class="text-caption font-weight-bold ms-1" style="color: #059669;">{{ myAssignedDriverTicketsCount }}</span>
                  </router-link>
                  <v-divider vertical class="mx-2 my-1"></v-divider>
                  <router-link to="/operations/support" class="cs-subbar-link d-flex align-center text-decoration-none">
                    <v-icon x-small color="#082C69" class="me-1">mdi-headset</v-icon>
                    <span class="text-caption font-weight-bold navy--text">المفتوحة:</span>
                    <span class="text-caption font-weight-bold ms-1" style="color: #0369A1;">{{ openDriverTicketsCount }}</span>
                  </router-link>
                </div>

                <!-- Notifications List (Scrollable, latest 15) -->
                <div class="cs-notif-list-container" v-if="opsNotifications.length > 0">
                  <div
                    v-for="(item, idx) in opsNotifications"
                    :key="'ops-notif-' + item.id"
                    class="cs-notif-item px-3 py-3 cursor-pointer"
                    :class="{ 'cs-notif-unread': item.seen === 0, 'cs-notif-read': item.seen === 1 }"
                    @click="markOpsNotificationSeen(item)"
                  >
                    <div class="d-flex align-start">
                      <!-- Semantic Icon Avatar -->
                      <div
                        class="cs-notif-avatar me-3 mt-1"
                        :class="item.related_type === 'trip_group' || item.type === 'trip_group' ? 'avatar-refund' : 'avatar-ticket'"
                      >
                        <v-icon size="18" :color="item.related_type === 'trip_group' || item.type === 'trip_group' ? '#B45309' : '#082C69'">
                          {{ (item.related_type === 'trip_group' || item.type === 'trip_group')
                            ? 'mdi-folder-multiple-outline'
                            : (item.event_type === 'driver_reply' ? 'mdi-message-reply-text' : 'mdi-steering') }}
                        </v-icon>
                      </div>

                      <!-- Content Area -->
                      <div class="flex-grow-1 min-w-0">
                        <div class="d-flex align-center justify-space-between mb-1">
                          <div class="d-flex align-center flex-wrap gap-1">
                            <!-- Source Badge -->
                            <span
                              class="cs-source-badge"
                              :class="item.related_type === 'trip_group' || item.type === 'trip_group' ? 'source-badge-refund' : 'source-badge-ticket'"
                            >
                              {{ (item.related_type === 'trip_group' || item.type === 'trip_group') ? 'مجموعة تشغيل' : 'سائق' }}
                            </span>

                            <!-- Exact Reference Code (DRV-XXXXXX or G-XXXX) -->
                            <span v-if="item.reference_code" class="mono-code cs-ref-code font-weight-bold ms-1">
                              {{ item.reference_code }}
                            </span>

                            <!-- Urgent badge -->
                            <span v-if="item.is_urgent" class="cs-urgent-badge ms-1">
                              عاجل
                            </span>
                          </div>

                          <div class="d-flex align-center">
                            <span class="cs-notif-time text-caption text-muted ms-2">
                              {{ item.time_ago || item.created_at }}
                            </span>
                            <span v-if="item.seen === 0" class="cs-unread-indicator-dot ms-1" title="غير مقروء"></span>
                          </div>
                        </div>

                        <!-- Title -->
                        <div class="cs-notif-title text-caption font-weight-bold text-dark text-truncate">
                          {{ item.title }}
                        </div>

                        <!-- Body -->
                        <div class="cs-notif-body text-caption text-muted">
                          {{ item.message }}
                        </div>

                        <!-- Extra Context (Driver Name) -->
                        <div v-if="item.driver_name" class="cs-notif-meta d-flex align-center mt-1">
                          <span class="d-flex align-center me-2 text-caption">
                            <v-icon x-small color="#64748B" class="me-1">mdi-steering</v-icon>
                            {{ item.driver_name }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <v-divider v-if="idx < opsNotifications.length - 1" class="mt-3"></v-divider>
                  </div>
                </div>

                <!-- Empty State -->
                <div v-else class="text-center py-8 px-4">
                  <div class="cs-empty-icon-circle mx-auto mb-2">
                    <v-icon size="32" color="#94A3B8">mdi-bell-check-outline</v-icon>
                  </div>
                  <div class="text-subtitle-2 font-weight-bold navy--text">لا توجد إشعارات جديدة</div>
                  <div class="text-caption text-muted mt-1">
                    ستظهر هنا إشعارات تذاكر السائقين وتكليفات مجموعات التشغيل فور وصولها.
                  </div>
                </div>

                <!-- Card Footer -->
                <div class="cs-notif-footer px-3 py-2 d-flex align-center justify-space-between border-top">
                  <router-link to="/operations/support" class="text-caption font-weight-bold navy--text text-decoration-none">
                    <v-icon x-small color="#082C69" class="me-1">mdi-headset</v-icon>
                    تذاكر السائقين
                  </router-link>
                  <router-link to="/operations/routes" class="text-caption font-weight-bold" style="color: #B45309; text-decoration: none;">
                    <v-icon x-small color="#B45309" class="me-1">mdi-routes</v-icon>
                    مجموعات التشغيل والمسارات
                  </router-link>
                </div>
              </v-card>

              <!-- SuperAdmin Notification List -->
              <v-list v-else-if="driversNotification.count != 0 || complaintsNotification.count != 0 || emergencyNotification.count != 0 || documentUpdatesNotification.count != 0 || lateStartNotification.count != 0 || overdueMovementNotification.count != 0 || staleTrackingNotification.count != 0">
                <v-list-item @click="viewStaleTrackingTrip(staleTrackingNotification.alerts.length ? staleTrackingNotification.alerts[0].action_url : null)" link v-if="staleTrackingNotification.count != 0">
                  <v-list-item-icon class="me-2">
                    <v-icon size="22" color="error">mdi-wifi-off</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold error--text">⚠️ مشكلة في اتصال تتبع GPS</v-list-item-title>
                    <v-list-item-subtitle v-if="staleTrackingNotification.alerts.length" class="text-caption text-truncate" style="max-width: 240px;">
                      {{ staleTrackingNotification.alerts[0].message }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-badge inline color="error" :content="staleTrackingNotification.count"></v-badge>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="staleTrackingNotification.count != 0"></v-divider>
                <v-list-item @click="viewLateStartRequests" link v-if="lateStartNotification.count != 0">
                  <v-list-item-icon class="me-2">
                    <v-icon size="22" color="warning">{{ lateStartNotification.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>طلبات بدء متأخرة تنتظر المراجعة</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-badge inline color="warning" :content="lateStartNotification.count"></v-badge>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="lateStartNotification.count != 0"></v-divider>

                <v-list-item @click="viewLateStartRequests" link v-if="overdueMovementNotification.count != 0">
                  <v-list-item-icon class="me-2">
                    <v-icon size="22" color="error">{{ overdueMovementNotification.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>رحلات لم يبدأ الكابتن التحرك لها</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-badge inline color="error" :content="overdueMovementNotification.count"></v-badge>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="overdueMovementNotification.count != 0"></v-divider>

                <v-list-item @click="viewDrivers" link v-if="driversNotification.count != 0">
                  <v-list-item-icon class="me-2">
                    <v-icon size="22">{{ driversNotification.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t(driversNotification.title) }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-badge inline color="error" :content="driversNotification.count"></v-badge>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="driversNotification.count != 0"></v-divider>

                <v-list-item @click="viewDocumentUpdates" link v-if="documentUpdatesNotification.count != 0">
                  <v-list-item-icon class="me-2">
                    <v-icon size="22" color="warning">{{ documentUpdatesNotification.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t(documentUpdatesNotification.title) }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-badge inline color="warning" :content="documentUpdatesNotification.count"></v-badge>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="documentUpdatesNotification.count != 0"></v-divider>

                <v-list-item @click="viewComplaints" link v-if="complaintsNotification.count != 0">
                  <v-list-item-icon class="me-2">
                    <v-icon size="22">{{ complaintsNotification.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ complaintsNotification.title }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-badge inline color="error" :content="complaintsNotification.count"></v-badge>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="complaintsNotification.count != 0"></v-divider>
                <v-list-item @click="viewEmergencyRequests" link v-if="emergencyNotification.count != 0">
                  <v-list-item-icon class="me-2">
                    <v-icon size="22">{{ emergencyNotification.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t(emergencyNotification.title) }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-badge inline color="error" :content="emergencyNotification.count"></v-badge>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <v-list v-else>
                <v-list-item>
                  <v-list-item-title>{{ $t("no_notifications") }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- User Menu -->
            <app-bar-user-menu
              :admin-name="adminProfileStore.name"
              :admin-avatar="adminProfileStore.avatar"
            ></app-bar-user-menu>
          </div>
        </div>
      </div>
    </v-app-bar>

    <!-- Main Content Area -->
    <v-main class="main-wrapper">
      <div class="app-content-container boxed-container pa-6">
        <slot></slot>
      </div>
    </v-main>

    <!-- Footer -->
    <v-footer
      app
      inset
      color="transparent"
      absolute
      height="56"
      class="px-0"
    >
      <div v-if="showActivationBanner" class="boxed-container w-full">
        <div class="ml-auto">
          <v-btn
            color="error"
            size="x-large"
            elevated
            block
            depressed
            @click="$router.push('/activate-account')"
          >
            Please activate your account
          </v-btn>
        </div>
      </div>
    </v-footer>
  </v-app>
</template>

<script>
import { mdiMagnify, mdiBellOutline, mdiGithub } from '@mdi/js'
import VerticalNavMenu from './components/vertical-nav-menu/VerticalNavMenu.vue'
import ThemeSwitcher from './components/ThemeSwitcher.vue'
import LangSwitcher from './components/LangSwitcher.vue'
import AppBarUserMenu from './components/AppBarUserMenu.vue'
import { adminProfileStore } from "@/utils/helpers";
import { activationStore } from "@/utils/helpers";
import { getUserRole, getCurrentUser } from '@/services/AuthService';

export default {
  components: {
    VerticalNavMenu,
    ThemeSwitcher,
    LangSwitcher,
    AppBarUserMenu,
  },
  setup() {
    const role = getUserRole();
    if (role === 0 || role === 3 || role === 4 || role === 5) {
      activationStore.isActivated = true;
    }
    return { adminProfileStore, activationStore }
  },
  data() {
    return {
      driversNotification: {
        title: 'Drivers require review',
        icon: 'mdi-account-tie-hat',
        count: 0,
      },
      complaintsNotification: {
        title: 'Unresolved complaints',
        icon: 'mdi-comment-alert',
        count: 0,
      },
      emergencyNotification: {
        title: 'Emergency end requests',
        icon: 'mdi-alert-octagon',
        count: 0,
      },
      documentUpdatesNotification: {
        title: 'driver_document_update_requests',
        icon: 'mdi-file-document-edit-outline',
        count: 0,
      },
      lateStartNotification: {
        title: 'Late start requests',
        icon: 'mdi-clock-alert-outline',
        count: 0,
      },
      overdueMovementNotification: {
        title: 'Overdue trip start warnings',
        icon: 'mdi-alarm-light',
        count: 0,
      },
      staleTrackingNotification: {
        title: '⚠️ مشكلة في اتصال تتبع GPS',
        icon: 'mdi-wifi-off',
        count: 0,
        alerts: [],
      },
      // Customer Service Role 5 notifications
      openTicketsCount: 0,
      pendingRefundsCount: 0,
      csNotifications: [],
      lastCsNotificationId: null,
      processedCsNotificationIds: [],
      isCsNotificationsInitialLoaded: false,
      // Operations Role 4 notifications
      openDriverTicketsCount: 0,
      unassignedDriverTicketsCount: 0,
      myAssignedDriverTicketsCount: 0,
      opsNotifications: [],
      lastOpsNotificationId: null,
      processedOpsNotificationIds: [],
      isOpsNotificationsInitialLoaded: false,
      notificationAudio: null,
      audioUnlocked: false,
      isLoadingNotifications: false,
      latestPendingDriverId: null,
      secureKey: null,
      isDrawerOpen: false, // Default closed on load
      
      // Icons
      icons: {
        mdiMagnify,
        mdiBellOutline,
        mdiGithub,
      },

      // Desktop Top Navigation Links & Dropdowns
      navMenus: [
        {
          title: 'Buses',
          icon: 'mdi-bus-multiple',
          items: [
            { title: 'Buses', to: '/buses', icon: 'mdi-bus' }
          ]
        },
        {
          title: 'Routes',
          icon: 'mdi-road-variant',
          items: [
            { title: 'Routes', to: '/routes', icon: 'mdi-road' }
          ]
        },
        {
          title: 'Stops',
          icon: 'mdi-bus-stop',
          items: [
            { title: 'Stops', to: '/stops', icon: 'mdi-map-marker-radius' }
          ]
        },
        {
          title: 'Trips',
          icon: 'mdi-bus-clock',
          items: [
            { title: 'Trips', to: '/trips', icon: 'mdi-bus-clock' }
          ]
        },
        {
          title: 'Reservations',
          icon: 'mdi-poll',
          isDirect: true,
          to: { name: 'reservations' }
        },
        {
          title: 'Regular Subscriptions',
          icon: 'mdi-wallet-membership',
          items: [
            { title: 'Subscription Plans', to: '/subscription-plans', icon: 'mdi-card-text-outline' },
            { title: 'Trips Subscription', to: '/trip-subscriptions', icon: 'mdi-account-card-outline' },
            { title: 'Route Passes', to: '/route-subscription-passes', icon: 'mdi-card-account-details-outline' }
          ]
        },
        {
          title: 'Payments',
          icon: 'mdi-cash-multiple',
          items: [
            { title: 'Upcoming Payments', to: '/upcoming-payments', icon: 'mdi-clock-outline' },
            { title: 'Redemptions', to: '/redemptions', icon: 'mdi-cash-check' },
            { title: 'Driver Earnings Report', to: '/driver-earnings-report', icon: 'mdi-chart-areaspline-variant' },
            { title: 'الاستردادات', to: '/customer-service/refunds', icon: 'mdi-cash-refund' }
          ]
        },
        {
          title: 'Users',
          icon: 'mdi-account-group',
          items: [
            { title: 'Customers', to: '/customers', icon: 'mdi-account' },
            { title: 'Drivers', to: '/drivers', icon: 'mdi-account-tie-hat' }
          ]
        },
        {
          title: 'Private Car Booking',
          icon: 'mdi-car',
          items: [
            { title: 'Car Types', to: '/private-car-bookings/types', icon: 'mdi-shape-outline' },
            { title: 'Cars', to: '/private-car-bookings/cars', icon: 'mdi-car-info' },
            { title: 'Private Car Requests', to: '/private-car-bookings/requests', icon: 'mdi-file-document-outline' }
          ]
        },
        {
          title: 'driver_services',
          icon: 'mdi-tow-truck',
          items: [
            { title: 'towing_services', to: '/driver-services/towing', icon: 'mdi-tow-truck' },
            { title: 'repair_centers', to: '/driver-services/repair-centers', icon: 'mdi-wrench-clock' },
            { title: 'driver_faqs', to: '/driver-services/faqs', icon: 'mdi-help-circle-outline' }
          ]
        },
        {
          title: 'More',
          icon: 'mdi-dots-horizontal-circle-outline',
          items: [
            { title: 'Live Tracking', to: { name: 'live-tracking' }, icon: 'mdi-crosshairs-gps', isLive: true },
            { title: 'Time Table', to: { name: 'planned-trips' }, icon: 'mdi-airplane-clock' },
            { title: 'Coupons', to: '/coupons', icon: 'mdi-ticket-percent' },
            { isDivider: true },
            { title: 'Issues', isHeader: true },
            { title: 'Complaints', to: '/complaints', icon: 'mdi-comment-alert' },
            { title: 'تذاكر السائقين (التشغيل)', to: '/operations/support', icon: 'mdi-headset' },
            { title: 'Driver Conflicts', to: '/driver-conflicts', icon: 'mdi-alert-circle' },
            { title: 'Emergency End Requests', to: '/driver-emergency-requests', icon: 'mdi-alert-octagon' },
            { isDivider: true },
            { title: 'Settings', isHeader: true },
            { title: 'General Settings', to: '/settings', icon: 'mdi-cog-outline' },
            { title: 'driver_wallet_settings', to: '/driver-wallet-settings', icon: 'mdi-wallet-outline' },
            { title: 'Admins', to: '/admin', icon: 'mdi-shield-account' },
            { title: 'Privacy Policy', to: '/privacy-policy', icon: 'mdi-shield-check' },
            { title: 'Terms & Conditions', to: '/terms-and-conditions', icon: 'mdi-file-document-outline' }
          ]
        }
      ]
    }
  },
  computed: {
    isSuperAdmin() {
      const r = Number(getUserRole());
      if (r === 0) return true;
      try {
        const u = getCurrentUser();
        if (u && (Number(u.role) === 0 || Number(u.role_id) === 0 || Number(u.user_type) === 0)) return true;
      } catch (e) {}
      return false;
    },
    isRouteMaking() {
      const r = Number(getUserRole());
      if (r === 3) return true;
      try {
        const u = getCurrentUser();
        if (u && (Number(u.role) === 3 || Number(u.role_id) === 3 || Number(u.user_type) === 3)) return true;
      } catch (e) {}
      return false;
    },
    isOperations() {
      const r = Number(getUserRole());
      if (r === 4) return true;
      try {
        const u = getCurrentUser();
        if (u && (Number(u.role) === 4 || Number(u.role_id) === 4 || Number(u.user_type) === 4)) return true;
      } catch (e) {}
      return false;
    },
    isCustomerService() {
      const r = Number(getUserRole());
      if (r === 5) return true;
      try {
        const u = getCurrentUser();
        if (u && (Number(u.role) === 5 || Number(u.role_id) === 5 || Number(u.user_type) === 5)) return true;
      } catch (e) {}
      return false;
    },
    isInternalStaff() {
      const role = Number(getUserRole());
      if (role === 0 || role === 3 || role === 4 || role === 5) return true;
      try {
        const u = getCurrentUser();
        const ur = u ? Number(u.role !== undefined ? u.role : (u.role_id !== undefined ? u.role_id : u.user_type)) : null;
        if (ur === 0 || ur === 3 || ur === 4 || ur === 5) return true;
      } catch (e) {}
      return false;
    },
    showActivationBanner() {
      if (this.isInternalStaff) {
        return false;
      }
      return !this.activationStore.isActivated;
    },
    isRouteMakingRouteActive() {
      const p = this.$route ? this.$route.path : '';
      return p.startsWith('/routes') || p.startsWith('/system-setup/routes');
    },
    isRouteMakingTripsActive() {
      const p = this.$route ? this.$route.path : '';
      return p.startsWith('/trips');
    },
    isTripGroupsActive() {
      const p = this.$route ? this.$route.path : '';
      return p.startsWith('/trip-groups');
    },
    isOperationsRoutesActive() {
      const p = this.$route ? this.$route.path : '';
      return p.startsWith('/operations/routes');
    },
    isOperationsTripsActive() {
      const p = this.$route ? this.$route.path : '';
      return p.startsWith('/operations/trips');
    },
    isOperationsLiveTrackingActive() {
      const p = this.$route ? this.$route.path : '';
      return p.startsWith('/operations/live-tracking');
    },
    isOperationsSupportActive() {
      const p = this.$route ? this.$route.path : '';
      return p.startsWith('/operations/support');
    },
    isOperationsEmergencyActive() {
      const p = this.$route ? this.$route.path : '';
      return p.startsWith('/operations/emergency-requests') || p.startsWith('/driver-emergency-requests');
    },
    isCustomerServiceCustomersActive() {
      const p = this.$route ? this.$route.path : '';
      return p.startsWith('/customer-service/customers');
    },
    isCustomerServiceBookingsActive() {
      const p = this.$route ? this.$route.path : '';
      return p.startsWith('/customer-service/bookings');
    },
    isCustomerServiceRefundsActive() {
      const p = this.$route ? this.$route.path : '';
      return p.startsWith('/customer-service/refunds');
    },
    isCustomerServiceTicketsActive() {
      const p = this.$route ? this.$route.path : '';
      return p.startsWith('/customer-service/tickets');
    },
    isCsNotificationMode() {
      if (this.isCustomerService) return true;
      if (this.isSuperAdmin && (
        this.isCustomerServiceTicketsActive ||
        this.isCustomerServiceRefundsActive ||
        this.isCustomerServiceCustomersActive ||
        this.isCustomerServiceBookingsActive
      )) {
        return true;
      }
      return false;
    },
    isOpsNotificationMode() {
      if (this.isOperations) return true;
      if (this.isSuperAdmin && this.isOperationsSupportActive) {
        return true;
      }
      return false;
    },
    unreadCsNotifsCount() {
      if (!this.csNotifications || !this.csNotifications.length) return 0;
      return this.csNotifications.filter(n => n.seen === 0).length;
    },
    unreadOpsNotifsCount() {
      if (!this.opsNotifications || !this.opsNotifications.length) return 0;
      return this.opsNotifications.filter(n => n.seen === 0).length;
    },
    csTotalBadgeCount() {
      return (this.openTicketsCount || 0) + (this.pendingRefundsCount || 0);
    },
  },
  mounted() {
    this.initNotificationAudio();
    if (this.isSuperAdmin || this.isCustomerService || this.isOperations) {
      this.loadNotifications();
      const intervalMs = (this.isCustomerService || this.isOperations || this.isSuperAdmin) ? 2500 : 10000;
      this.notificationInterval = window.setInterval(() => {
        if (!document.hidden) {
          this.loadNotifications();
        }
      }, intervalMs);
    }
  },
  beforeDestroy() {
    if (this.notificationInterval) {
      clearInterval(this.notificationInterval);
    }
    if (this._audioUnlockCleanup) {
      this._audioUnlockCleanup();
    }
  },
  methods: {
    initNotificationAudio() {
      try {
        const unlock = () => {
          if (this.audioUnlocked) return;
          const a = new Audio('/sounds/beepgo_notification.mp3');
          a.volume = 0.01;
          const p = a.play();
          if (p !== undefined) {
            p.then(() => {
              a.pause();
              a.currentTime = 0;
              this.audioUnlocked = true;
              console.log('[BeepGo Audio] Audio unlocked on user gesture');
              cleanup();
            }).catch(() => {});
          }
        };
        const cleanup = () => {
          window.removeEventListener('pointerdown', unlock);
          window.removeEventListener('click', unlock);
          window.removeEventListener('keydown', unlock);
        };
        this._audioUnlockCleanup = cleanup;
        window.addEventListener('pointerdown', unlock);
        window.addEventListener('click', unlock);
        window.addEventListener('keydown', unlock);

        // Global authoritative sound gate callable from anywhere (Content or Tickets page)
        window.__beepgoAuthoritativeSoundGate = (keys) => {
          this.playAuthoritativeIncomingSound(keys);
        };
      } catch (e) {
        console.warn('Audio init error:', e);
      }
    },
    playAuthoritativeIncomingSound(keys) {
      if (!keys) return;
      const keyList = Array.isArray(keys) ? keys : [keys];
      if (!window.__beepgoProcessedSoundKeys) {
        window.__beepgoProcessedSoundKeys = new Set();
      }
      const alreadyPlayed = keyList.some(k => window.__beepgoProcessedSoundKeys.has(k));
      if (alreadyPlayed) {
        return;
      }
      keyList.forEach(k => window.__beepgoProcessedSoundKeys.add(k));
      this.playNotificationSound();
    },
    playNotificationSound() {
      try {
        const audio = new Audio('/sounds/beepgo_notification.mp3');
        audio.volume = 1.0;
        const p = audio.play();
        if (p !== undefined) {
          p.then(() => {
            console.log('[BeepGo Audio] Custom incoming sound played successfully');
          }).catch(err => {
            console.warn('[BeepGo Audio] Play sound failed / blocked:', err);
          });
        }
      } catch (e) {
        console.warn('[BeepGo Audio] Play error:', e);
      }
    },
    getCurrentUserId() {
      try {
        const u = getCurrentUser();
        return u && u.id ? parseInt(u.id, 10) : null;
      } catch (e) {
        return null;
      }
    },
    markCsNotificationSeen(item) {
      if (!item) return;
      if (!this.processedCsNotificationIds.includes(item.id)) {
        this.processedCsNotificationIds.push(item.id);
      }
      if (item.seen === 0) {
        item.seen = 1;
        axios.post('/notifications/mark-as-seen', { id: item.id }).catch(() => {});
      }
      if (item.action_url) {
        const currentPath = this.$route.path;
        const targetUrl = item.action_url;
        const [targetPath, targetQueryStr] = targetUrl.split('?');
        const targetQuery = {};
        if (targetQueryStr) {
          const pairs = targetQueryStr.split('&');
          pairs.forEach(p => {
            const [k, v] = p.split('=');
            if (k) targetQuery[k] = decodeURIComponent(v || '');
          });
        }

        if (currentPath === targetPath) {
          this.$router.push({ path: targetPath, query: targetQuery }).catch(() => {});
        } else {
          this.$router.push(targetUrl).catch(() => {});
        }
      }
    },
    markAllCsNotificationsSeen() {
      if (!this.csNotifications || !this.csNotifications.length) return;
      this.csNotifications.forEach(n => { n.seen = 1; });
      axios.post('/notifications/mark-all-as-seen', { scope: 'customer_service' }).catch(err => {
        console.warn('Failed to mark all CS notifications seen:', err);
      });
    },
    markOpsNotificationSeen(item) {
      if (!item) return;
      if (!this.processedOpsNotificationIds.includes(item.id)) {
        this.processedOpsNotificationIds.push(item.id);
      }
      if (item.seen === 0) {
        item.seen = 1;
        axios.post('/notifications/mark-as-seen', { id: item.id }).catch(() => {});
      }
      if (item.action_url) {
        const currentPath = this.$route.path;
        const targetUrl = item.action_url;
        const [targetPath, targetQueryStr] = targetUrl.split('?');
        const targetQuery = {};
        if (targetQueryStr) {
          const pairs = targetQueryStr.split('&');
          pairs.forEach(p => {
            const [k, v] = p.split('=');
            if (k) targetQuery[k] = decodeURIComponent(v || '');
          });
        }

        if (currentPath === targetPath) {
          this.$router.push({ path: targetPath, query: targetQuery }).catch(() => {});
        } else {
          this.$router.push(targetUrl).catch(() => {});
        }
      }
    },
    markAllOpsNotificationsSeen() {
      if (!this.opsNotifications || !this.opsNotifications.length) return;
      this.opsNotifications.forEach(n => { n.seen = 1; });
      axios.post('/notifications/mark-all-as-seen', { scope: 'operations' }).catch(err => {
        console.warn('Failed to mark all Operations notifications seen:', err);
      });
    },
    loadNotifications() {
      if (this.isLoadingNotifications) return;
      this.isLoadingNotifications = true;
      axios.get('/notifications/all').then((response) => {
        if (response && response.data) {
          this.complaintsNotification.count = response.data.unResolvedComplaintsCount || 0;
          this.driversNotification.count = response.data.driversUnderReviewCount || 0;
          this.emergencyNotification.count = response.data.pendingEmergencyRequestsCount || 0;
          this.documentUpdatesNotification.count = response.data.pendingDocumentUpdatesCount || 0;
          this.lateStartNotification.count = response.data.pendingLateStartRequestsCount || 0;
          this.overdueMovementNotification.count = response.data.overdueMovementCount || 0;
          this.staleTrackingNotification.count = response.data.staleTrackingCount || 0;
          this.staleTrackingNotification.alerts = response.data.staleTrackingAlerts || [];
          this.latestPendingDriverId = response.data.latestPendingDriverId || null;
          if (response.data.adminName) this.adminProfileStore.name = response.data.adminName;
          if (response.data.adminAvatar) this.adminProfileStore.avatar = response.data.adminAvatar;
          this.secureKey = response.data.secureKey;

          if (response.data.openTicketsCount !== undefined) {
            this.openTicketsCount = response.data.openTicketsCount;
          }
          if (response.data.pendingRefundsCount !== undefined) {
            this.pendingRefundsCount = response.data.pendingRefundsCount;
          }
          if (response.data.openDriverTicketsCount !== undefined) {
            this.openDriverTicketsCount = response.data.openDriverTicketsCount;
          }
          if (response.data.unassignedDriverTicketsCount !== undefined) {
            this.unassignedDriverTicketsCount = response.data.unassignedDriverTicketsCount;
          }
          if (response.data.myAssignedDriverTicketsCount !== undefined) {
            this.myAssignedDriverTicketsCount = response.data.myAssignedDriverTicketsCount;
          }

          if (response.data.customerServiceNotifications) {
            const newNotifs = response.data.customerServiceNotifications || [];
            const latestId = response.data.latestCsNotificationId || 0;

            if (!this.isCsNotificationsInitialLoaded) {
              // Section 5: INITIAL PAGE LOAD
              // Establish baseline from existing notifications without playing sound
              this.processedCsNotificationIds = newNotifs.map(n => n.id);
              this.lastCsNotificationId = latestId || (newNotifs.length ? Math.max(...newNotifs.map(n => n.id)) : 0);
              this.isCsNotificationsInitialLoaded = true;
            } else {
              // Section 4, 6, 7: STRICT INCOMING AUDIO GATE
              const currentUserId = this.getCurrentUserId();
              const allowedIncomingEvents = ['ticket_created', 'customer_reply', 'refund_created', 'refund_submitted'];

              for (const notif of newNotifs) {
                // Deduplication: skip if already processed in this browser session
                if (this.processedCsNotificationIds.includes(notif.id)) {
                  continue;
                }

                const notifId = parseInt(notif.id, 10);
                const isNewer = !this.lastCsNotificationId || notifId > this.lastCsNotificationId;
                const isUnread = notif.seen === 0;
                const isRecipientCs = this.isCustomerService || this.isSuperAdmin;

                // Section 6: Staff self-event protection
                const notifActorId = notif.actor_user_id !== null && notif.actor_user_id !== undefined
                  ? parseInt(notif.actor_user_id, 10)
                  : (notif.data && notif.data.actor_user_id ? parseInt(notif.data.actor_user_id, 10) : null);
                const isSelfAction = currentUserId && notifActorId && notifActorId === currentUserId;

                const notifActorRole = notif.actor_role !== null && notif.actor_role !== undefined
                  ? parseInt(notif.actor_role, 10)
                  : (notif.data && notif.data.actor_role !== undefined ? parseInt(notif.data.actor_role, 10) : null);
                const isStaffOriginated = notif.origin === 'staff_originated' ||
                  (notif.data && notif.data.origin === 'staff_originated') ||
                  (notifActorRole !== null && (notifActorRole === 0 || notifActorRole === 5));

                const eventType = notif.event_type ||
                  (notif.data && (notif.data.event_type || notif.data.event)) ||
                  notif.type;
                const isAllowedEvent = allowedIncomingEvents.includes(eventType);
                const isCustomerOriginated = notif.origin === 'customer_originated' ||
                  (notif.data && notif.data.origin === 'customer_originated') ||
                  isAllowedEvent ||
                  (eventType && eventType.startsWith('customer_'));

                // Play only when ALL conditions are met
                if (
                  isNewer &&
                  isUnread &&
                  isRecipientCs &&
                  !isSelfAction &&
                  !isStaffOriginated &&
                  isCustomerOriginated &&
                  isAllowedEvent
                ) {
                  const soundKeys = ['notif_' + notif.id];
                  if (notif.message_id) {
                    soundKeys.push('msg_' + notif.message_id);
                  }
                  if (notif.related_type === 'ticket' && notif.related_id) {
                    soundKeys.push('ticket_event_' + notif.related_id + '_' + notif.id);
                  }
                  this.playAuthoritativeIncomingSound(soundKeys);

                  // Dispatch event for ticket attention markers / live view
                  try {
                    window.dispatchEvent(new CustomEvent('beepgo:cs-activity', {
                      detail: {
                        ticketId: notif.related_id ? parseInt(notif.related_id, 10) : null,
                        eventType: eventType,
                        notificationId: notif.id
                      }
                    }));
                  } catch (e) {}
                }

                // Register as processed to guarantee zero repeat sounds
                this.processedCsNotificationIds.push(notif.id);
              }

              if (latestId && (!this.lastCsNotificationId || latestId > this.lastCsNotificationId)) {
                this.lastCsNotificationId = latestId;
              }
            }

            this.csNotifications = newNotifs;
          }

          // Operations Role 4 Notifications & Live Incoming Sound Gate
          if (response.data.operationsNotifications) {
            const newOpsNotifs = response.data.operationsNotifications || [];
            const latestOpsId = response.data.latestOpsNotificationId || 0;

            if (!this.isOpsNotificationsInitialLoaded) {
              // Establish baseline on initial load without playing sound
              this.processedOpsNotificationIds = newOpsNotifs.map(n => n.id);
              this.lastOpsNotificationId = latestOpsId || (newOpsNotifs.length ? Math.max(...newOpsNotifs.map(n => n.id)) : 0);
              this.isOpsNotificationsInitialLoaded = true;
            } else {
              const currentUserId = this.getCurrentUserId();
              const allowedOpsIncomingEvents = ['driver_ticket_created', 'driver_reply', 'trip_group_assigned'];

              for (const notif of newOpsNotifs) {
                if (this.processedOpsNotificationIds.includes(notif.id)) {
                  continue;
                }

                const notifId = parseInt(notif.id, 10);
                const isNewer = !this.lastOpsNotificationId || notifId > this.lastOpsNotificationId;
                const isUnread = notif.seen === 0;
                const isRecipientOps = this.isOperations || this.isSuperAdmin;

                const notifActorId = notif.actor_user_id !== null && notif.actor_user_id !== undefined
                  ? parseInt(notif.actor_user_id, 10)
                  : (notif.data && notif.data.actor_user_id ? parseInt(notif.data.actor_user_id, 10) : null);
                const isSelfAction = currentUserId && notifActorId && notifActorId === currentUserId;

                const eventType = notif.event_type ||
                  (notif.data && (notif.data.event_type || notif.data.event)) ||
                  notif.type;
                const isAllowedOpsEvent = allowedOpsIncomingEvents.includes(eventType);

                // Check ownership eligibility on driver_reply (must be unassigned or assigned to current user or SuperAdmin)
                const assignedOwnerId = notif.assigned_to !== null && notif.assigned_to !== undefined
                  ? parseInt(notif.assigned_to, 10)
                  : null;
                const isEligibleForThisUser = this.isSuperAdmin ||
                  !assignedOwnerId ||
                  (currentUserId && assignedOwnerId === currentUserId);

                if (
                  isNewer &&
                  isUnread &&
                  isRecipientOps &&
                  !isSelfAction &&
                  isAllowedOpsEvent &&
                  isEligibleForThisUser
                ) {
                  const soundKeys = ['ops_notif_' + notif.id];
                  if (notif.message_id) {
                    soundKeys.push('msg_' + notif.message_id);
                  }
                  this.playAuthoritativeIncomingSound(soundKeys);

                  try {
                    window.dispatchEvent(new CustomEvent('beepgo:ops-activity', {
                      detail: {
                        ticketId: notif.related_type === 'driver_ticket' && notif.related_id ? parseInt(notif.related_id, 10) : null,
                        groupId: notif.related_type === 'trip_group' && notif.related_id ? parseInt(notif.related_id, 10) : null,
                        eventType: eventType,
                        notificationId: notif.id
                      }
                    }));
                  } catch (e) {}
                }

                this.processedOpsNotificationIds.push(notif.id);
              }

              if (latestOpsId && (!this.lastOpsNotificationId || latestOpsId > this.lastOpsNotificationId)) {
                this.lastOpsNotificationId = latestOpsId;
              }
            }

            this.opsNotifications = newOpsNotifs;
          }

          const role = getUserRole();
          if (role === 0 || role === 3 || role === 4 || role === 5) {
            this.activationStore.isActivated = true;
          } else if (this.secureKey == null) {
            this.activationStore.isActivated = false;
          } else {
            this.activationStore.isActivated = true;
          }
        }
      }).catch((err) => {
        console.error("Notifications error:", err);
      }).finally(() => {
        this.isLoadingNotifications = false;
      });
    },
    viewStaleTrackingTrip(actionUrl) {
      if (actionUrl) {
        this.$router.push(actionUrl);
      } else {
        this.$router.push('/planned-trips');
      }
    },
    viewLateStartRequests() {
      localStorage.tabIdxTrips = 3;
      this.$router.push('/trips');
    },
    viewDrivers() {
      localStorage.tabIdxDrivers = 2;
      this.$router.push('/drivers');
    },
    viewDocumentUpdates() {
      if (this.latestPendingDriverId) {
        this.$router.push({ path: '/users/view', query: { user: this.latestPendingDriverId } });
      } else {
        this.$router.push('/drivers');
      }
    },
    viewComplaints() {
      localStorage.tabIdxComplaints = 0;
      this.$router.push('/complaints');
    },
    viewEmergencyRequests() {
      this.$router.push('/driver-emergency-requests');
    },
    viewCancelledTrips() {
      localStorage.tabIdxTrips = 4;
      this.$router.push('/trips');
    },
  }
}
</script>

<style lang="scss" scoped>
.app-bar-bg {
  border-bottom: 1px solid #E4E8F0 !important;
  background-color: #FFFFFF !important;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03) !important;
}

.main-wrapper {
  background-color: #F7F9FC !important;
}

.app-content-container {
  background-color: #F7F9FC !important;
  min-height: calc(100vh - 70px - 56px);
}

.nav-item {
  color: #082C69 !important; /* BeepGo Navy */
  font-size: var(--dashboard-font-size) !important;
  font-weight: 600 !important;
  letter-spacing: normal;
  height: 38px !important;
  margin: 0 1px !important;
  padding: 0 5px !important;
  border-radius: 8px;
  transition: all 0.2s ease;
  flex-shrink: 0 !important;
  
  &:hover {
    background-color: rgba(8, 28, 105, 0.05) !important;
  }

  @media (max-width: 1800px) {
    font-size: var(--dashboard-font-size) !important;
    padding: 0 3px !important;
    margin: 0 0.5px !important;
    
    ::v-deep .v-icon--left {
      display: none !important;
    }
  }
}

.nav-menu-container {
  overflow-x: auto !important;
  white-space: nowrap !important;
  -ms-overflow-style: none !important;  /* IE and Edge */
  scrollbar-width: none !important;  /* Firefox */
}

.nav-menu-container::-webkit-scrollbar {
  display: none !important; /* Chrome, Safari and Opera */
}

.active-nav-item {
  color: #F5A114 !important; /* BeepGo Gold */
  background-color: rgba(245, 161, 20, 0.08) !important;
  border-bottom: 2px solid #F5A114 !important;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.live-dot {
  width: 8px;
  height: 8px;
  background-color: #FF4D4F;
  border-radius: 50%;
  display: inline-block;
  animation: blink 1.2s infinite;
}

@keyframes blink {
  0% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 0.3; }
}

.boxed-container {
  max-width: 1560px;
  margin-left: auto;
  margin-right: auto;
}

.nav-menu-dropdown {
  border-radius: 8px !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid #E4E8F0 !important;
}

/* Role 5 Notification Center */
.cs-notification-card {
  border: 1px solid #CBD5E1 !important;
  box-shadow: 0 10px 25px -5px rgba(8, 44, 105, 0.15), 0 8px 10px -6px rgba(8, 44, 105, 0.1) !important;
  overflow: hidden;
  font-family: 'Cairo', sans-serif !important;
}

.cs-notif-header {
  background-color: #FFFFFF;
  border-bottom: 1px solid #E2E8F0;
}

.cs-notif-header-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: #EFF6FF;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cs-unread-counter-badge {
  background-color: #EF4444;
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 9999px;
  letter-spacing: 0.2px;
}

.mark-all-btn {
  font-size: 11px !important;
  background-color: #F1F5F9;
}

.cs-notif-subbar {
  background-color: #F8FAFC;
  border-bottom: 1px solid #E2E8F0;
}

.cs-subbar-link {
  transition: opacity 0.15s ease;
}
.cs-subbar-link:hover {
  opacity: 0.8;
}

.cs-notif-list-container {
  max-height: 420px;
  overflow-y: auto;
  background-color: #FFFFFF;
}

.cs-notif-item {
  transition: background-color 0.15s ease;
  position: relative;
}

.cs-notif-item:hover {
  background-color: #F8FAFC !important;
}

.cs-notif-unread {
  background-color: #F0F7FF;
  border-right: 3px solid #082C69;
}

.cs-notif-read {
  background-color: #FFFFFF;
  border-right: 3px solid transparent;
}

.cs-notif-avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-ticket {
  background-color: #E0E7FF;
  border: 1px solid #C7D2FE;
}

.avatar-refund {
  background-color: #FEF3C7;
  border: 1px solid #FDE68A;
}

.cs-source-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 5px;
  display: inline-block;
  letter-spacing: 0.2px;
}

.source-badge-ticket {
  background-color: #E0E7FF;
  color: #082C69;
  border: 1px solid #C7D2FE;
}

.source-badge-refund {
  background-color: #FEF3C7;
  color: #B45309;
  border: 1px solid #FDE68A;
}

.cs-ref-code {
  font-size: 11px;
  color: #082C69;
  background-color: #F1F5F9;
  padding: 1px 5px;
  border-radius: 4px;
}

.cs-urgent-badge {
  background-color: #FEE2E2;
  color: #DC2626;
  border: 1px solid #FECACA;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
}

.cs-unread-indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #2563EB;
  display: inline-block;
}

.cs-notif-time {
  font-size: 10.5px !important;
  color: #64748B !important;
  font-weight: 500;
}

.cs-notif-title {
  color: #0F172A !important;
  font-size: 12.5px;
  line-height: 1.4;
}

.cs-notif-body {
  color: #475569 !important;
  font-size: 11.5px !important;
  line-height: 1.45;
  margin-top: 2px;
}

.cs-notif-meta {
  color: #64748B;
  font-size: 11px;
}

.cs-empty-icon-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #F1F5F9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cs-notif-footer {
  background-color: #F8FAFC;
  border-top: 1px solid #E2E8F0;
}
</style>
